import {FC, useState, useEffect, useCallback, useRef} from "react";
import {makeStyles} from "tss-react/mui";
import {Box, Theme, useTheme, LinearProgress} from "@mui/material";
import {AdminHeader} from "../../AdminHeader";
import {IAdminUsersResponseEntry} from "../../../../types/admin";
import {AdminTable, AdminTableProps} from "@/components/common/table";
import {useNavigate} from "react-router-dom";
import {convertSecondsToHHMM} from "../../../../utils";
import {GRID_CHECKBOX_SELECTION_COL_DEF, GridRowId, GridRowModel, GridSelectionModel} from "@mui/x-data-grid-pro";
import useDevCycleSdk from "@/hooks/useDevCycleSdk";
import {Restore} from "@mui/icons-material";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import {CustomTooltip} from "@/components/common";
import useCustomSnackbar from "@/snackbars/useCustomSnackbar";
import {
	BulkRescheduleConfirmationModal
} from "@/pages/ManagerView/Pages/Users/BulkReschedule/BulkRescheduleConfirmationModal";
import {BulkActionStatus, useManageBulkActionStatusContext} from "@/contexts/manageBulkActionStatusContext";


const useStyles = makeStyles()((_theme) => ({
	adminUsersList: {
		"& th": {
			fontSize: "11px",
			fontWeight: 700,
			textTransform: "uppercase"
		}
	},
	enabledCustomToolbar: {
		"&.MuiBox-root": {
			padding: 0,
			color: _theme.palette.primary.main,
			cursor: "pointer",
			fontSize: "0.8125rem",
			fontWeight: 600,
			"& svg": {
				"& path": {
					fill: _theme.palette.primary.main
				},
				color: _theme.palette.primary.main,
				top: "5px",
				position: "relative"
			},
			top: "-3px",
			position: "relative",
			transition: "opacity .3s",
			"&:hover": {
				opacity: .8
			}
		}
	},
	disabledCustomToolbar: {
		"&.MuiBox-root": {
			padding: 0,
			color: _theme.palette.action.disabled,
			cursor: "default",
			fontSize: "0.8125rem",
			fontWeight: 600,
			"& svg": {
				"& path": {
					fill: _theme.palette.action.disabled
				},
				color: _theme.palette.action.disabled,
				top: "5px",
				position: "relative"
			},
			top: "-3px",
			position: "relative"
		}
	}
}));

const getHeadCells = (theme: Theme): AdminTableProps<"user", IAdminUsersResponseEntry>["columns"] => ([
	{
		...GRID_CHECKBOX_SELECTION_COL_DEF,
		type: "string",
		hideable: false
	},
	{
		field: "firstname",
		flex: 1,
		headerName: "First Name",
		type: "string"
	},
	{
		field: "lastname",
		flex: 1,
		headerName: "Last Name",
		type: "string"
	},
	{
		field: "jobCategory",
		flex: 1,
		headerName: "Job Category",
		type: "string"
	},
	{
		field: "timeSpentOnTraining",
		flex: 1,
		type: "number",
		maxWidth: 110,
		headerName: "Total Watch Time (HH:MM)",
		headerAlign: "left",
		align: "left",
		filterable: false,
		valueGetter: (e) => e.value ? convertSecondsToHHMM(e.value) : "0"
	},
	{
		field: "email",
		flex: 1,
		headerName: "Email",
		type: "string"
	},
	{
		field: "phone",
		flex: 1,
		headerName: "Phone",
		type: "string"
	},
	{
		field: "streak",
		flex: 1,
		type: "number",
		maxWidth: 110,
		headerName: "Streak",
		headerAlign: "left",
		align: "left",
		renderCell: (e) => {
			let textColor
			switch (true) {
				case e.value === 0:
					textColor = theme.palette.error.main
					break;
				case e.value > 0 && e.value <= 4:
					textColor = theme.palette.warning.main
					break;
				case e.value > 4:
					textColor = theme.palette.success.main
					break;
			}
			return <Box color={textColor} component="span">{e.value}</Box>
		}
	},
	{
		field: "jobTitle",
		flex: 1,
		headerName: "Job Title",
		type: "string"
	},
	{
		field: "completedModules",
		flex: 1,
		type: "number",
		maxWidth: 110,
		headerAlign: "left",
		align: "left",
		headerName: "Completed Lessons"
	},
	{
		field: "overdue",
		flex: 1,
		type: "number",
		maxWidth: 110,
		headerName: "Overdue Lessons",
		headerAlign: "left",
		align: "left",
		renderCell: (e) => {
			let textColor
			switch (true) {
				case e.value === 0:
					textColor = theme.palette.success.main
					break;
				case e.value > 0 && e.value <= 4:
					textColor = theme.palette.warning.main
					break;
				case e.value > 4:
					textColor = theme.palette.error.main
					break;
			}
			return <Box color={textColor} component="span">{e.value}</Box>
		}
	},
	{
		field: "createdAt",
		flex: 1,
		type: "date",
		headerName: "Created",
		valueGetter: (e) => e.value ? new Date(e.value).toLocaleDateString() : ""
	},
	{
		field: "division",
		flex: 1,
		type: "string",
		headerName: "Division"
	},
	{
		field: "supervisorName",
		flex: 1,
		type: "string",
		headerName: "Supervisor"
	}
]);

export const AdminUsersList: FC = () => {
	const {t} = useSSTranslation();
	const {classes} = useStyles();
	const navigate = useNavigate();
	const {rescheduleOverdueModules: {rescheduleOverdueStatus, setRescheduleOverdueStatus, updateRescheduleOverdueSnackbarId}} = useManageBulkActionStatusContext();
	const [rescheduleOverdueStatusAtMounting, setRescheduleOverdueStatusAtMounting] = useState(rescheduleOverdueStatus);
	const {closeSnackbar, errorSnackbar, processInProgressSnackbar} = useCustomSnackbar();
	const [selectedUserWithOverdueLessons, setSelectedUserWithOverdueLessons] = useState<GridSelectionModel>([]);
	const theme = useTheme();
	const {bulkRescheduleOverdue} = useDevCycleSdk();
	const headCells = getHeadCells(theme)
	const toolbarOptionClass = selectedUserWithOverdueLessons.length > 0 ? classes.enabledCustomToolbar : classes.disabledCustomToolbar;
	const [bulkRescheduleConfirmationModalOpen, setBulkRescheduleConfirmationModalOpen] = useState<boolean>(false);
	const refreshDataGridRef = useRef<(() => void) | null>(null);
	const clearSelectionDataGridRef = useRef<(() => void) | null>(null);
	const isRescheduleLoading = rescheduleOverdueStatus === BulkActionStatus.inProgress;

	useEffect(() => {
		if (rescheduleOverdueStatus === BulkActionStatus.complete) {
			if (refreshDataGridRef.current && rescheduleOverdueStatusAtMounting !== BulkActionStatus.complete) {
				refreshDataGridRef.current();
			}

			setRescheduleOverdueStatus(BulkActionStatus.none);
			setRescheduleOverdueStatusAtMounting(BulkActionStatus.none);
		}
	}, [rescheduleOverdueStatus]);

	// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
	const handleSelectRow = (selectedRows: Map<GridRowId, GridRowModel>) => {
		const selectedUserIds: number[] = [];
		selectedRows.forEach(row => {
			if (row && typeof row.overdue === "number" && row.overdue > 0) {
				selectedUserIds.push(row.id);
			}
		});
		setSelectedUserWithOverdueLessons(selectedUserIds);
	}

	// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
	const handleRescheduleOverdue = () => { setBulkRescheduleConfirmationModalOpen(true) }

	// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
	const handleRescheduleOverdueClose = () => {
		setBulkRescheduleConfirmationModalOpen(false);
	};

	// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
	const handleRescheduleLoadingChange = (isLoading: boolean) => {
		if (isLoading) {
			setRescheduleOverdueStatus(BulkActionStatus.inProgress);
		}
	};

	// Define handler outside useEffect to persist across re-renders
	const handleRescheduleCompletion = useCallback((data: any) => {
		// Handle different response formats from server
		let inProgress = false;
		clearSelectionDataGridRef.current && clearSelectionDataGridRef.current();
		setSelectedUserWithOverdueLessons([]);

		if (typeof data === "boolean") {
			inProgress = data;
		} else if (data && typeof data === "object") {
			if (data.inProgress !== undefined) {
				inProgress = data.inProgress;
			} else if (data.isSuccessful !== undefined) {
				inProgress = data.isSuccessful;
			} else {
				inProgress = false; // Default to false for unknown structure
			}
		} else {
			inProgress = false;
		}

		if (inProgress) {
			// Show progress toast (replicating Team Progress behavior)
			const toastId = processInProgressSnackbar(
				t("bulk_reschedule_progress_msg", "Rescheduling overdue lessons… Don't close or refresh this window."),
				true,
				true
			);
			updateRescheduleOverdueSnackbarId(toastId);
		} else {
			setRescheduleOverdueStatus(BulkActionStatus.none);
			errorSnackbar(
				t("bulk_reschedule_error_msg", "Unable to reschedule lessons. Please try again."),
				true
			);
		}
	}, [closeSnackbar, processInProgressSnackbar, errorSnackbar, t]);

	// Register listener for custom window event (inProgress callback)
	useEffect(() => {
		// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
		const handleWindowEvent = (event: CustomEvent) => {
			handleRescheduleCompletion(event.detail);
		};

		window.addEventListener("rescheduleOverdueModulesCompleted", handleWindowEvent as EventListener);

		return () => {
			window.removeEventListener("rescheduleOverdueModulesCompleted", handleWindowEvent as EventListener);
		};
	}, [handleRescheduleCompletion]);

	return (
		<Box className={classes.adminUsersList}>
			<AdminHeader title="Learner Progress" subtitle={t("admin_users_list_subtitle", "View and manage learner progress")} />
			<AdminTable<"user", IAdminUsersResponseEntry>
				url="/api/user"
				onEditAction={e => navigate("/admin/users/" + e.id.toString())}
				columns={headCells}
				type="user"
				disableColumnResize={false}
				// components={{Toolbar: (): JSX.Element => (<CustomToolbar exportButton columnsButton></CustomToolbar>)}}
				filterSettingsPersistenceStorageKey="employeeFilterSettings"
				initialState={{
					columns: {
						columnVisibilityModel: {
							createdAt: false,
							phone: false,
							workload: false,
							email: false,
							jobTitle: false
						}
					}
				}}
				customToolbarOptions={bulkRescheduleOverdue ?
					<CustomTooltip
						title={t("common_reschedule_overdue_tooltip_msg", "Reschedule overdue lessons for selected users")}
					>
						<Box
							className={isRescheduleLoading || selectedUserWithOverdueLessons.length === 0 ? classes.disabledCustomToolbar : toolbarOptionClass}
							onClick={isRescheduleLoading || selectedUserWithOverdueLessons.length === 0 ? undefined : handleRescheduleOverdue}
							sx={{position: "relative"}}
						>
							{isRescheduleLoading && (
								<LinearProgress
									sx={{
										position: "absolute",
										top: 0,
										left: 0,
										right: 0,
										height: "2px"
									}}
								/>
							)}
							<Restore sx={{height: "18px", width: "18px"}} /> {t("common_reschedule_overdue", "Reschedule Overdue").toUpperCase()}
						</Box>
					</CustomTooltip> :
					undefined
				}
				rowSelectionCallback={handleSelectRow}
			>
				{(_, reload, __, ___, clearSelection) => {
					// Store the reload function in the ref
					refreshDataGridRef.current = reload;
					clearSelectionDataGridRef.current = clearSelection;
					return null;
				}}
			</AdminTable>
			<BulkRescheduleConfirmationModal
				open={bulkRescheduleConfirmationModalOpen}
				onClose={handleRescheduleOverdueClose}
				selectedUserIds={selectedUserWithOverdueLessons.filter(id => typeof id === "number") as number[]}
				onLoadingChange={handleRescheduleLoadingChange}
			/>
		</Box>
	);
}
